import UKPageLoading from '@/components/UKPageLoading';
import { useTranslation } from '@/i18n/client';
import { addCartUtil } from '@/utils/cartUtils';
import to from 'await-to-js';
import cn from 'clsx';
import { observer } from 'mobx-react';
import { lazy, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import { fetchRecordDetail } from '@/apis/afterSales/afterSales.api';
import { IRecordDetailReq, RecordDetailResVo } from '@/apis/afterSales/afterSales.interface';

import RuleWrap from '@/app/csr-src/pages/order/components/ruleWrap';
import DetailProduct from './components/detailProduct';
import RefundDetails from './components/refundDetails';
import RefundInformation from './components/refundInformation';
import StepBar from './components/stepBar';

import styles from './index.module.scss';
// 埋点
import { SceneCode } from '@/apis/recommend/recommend.interface';
import UKPageError from '@/components/UKPageError';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';
import { sendPv } from '@/tracks/25328';
import AfterSalesAccordion from './components/afterSalesAccordion';

const RecommendClient = lazy(() => import('@/components/recommend/recommendClient'));

const AfterSales = observer(() => {
  const [afsDetails, setAfsDetails] = useState<RecordDetailResVo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const afsServiceId = queryParams.get('afsServiceId') || ''; // 获取服务单ID
  const orderId = queryParams.get('orderId') || ''; // 获取订单ID

  const loadAfsDetails = async (cb?: any) => {
    const requestData: IRecordDetailReq = {
      afsServiceId: String(afsServiceId),
      orderId: String(orderId),
    };
    const [err, recordDetailData] = await to(fetchRecordDetail(requestData));

    if (err) {
      setError('Failed to fetch order details');
      // setAfsDetails(null);
    } else {
      setAfsDetails(recordDetailData as RecordDetailResVo);
      cb && cb();
      setError(null);
    }

    setLoading(false);
    setIsLoaded(true);
  };

  useEffect(() => {
    // 退货详情页浏览埋点
    sendPv.Return_Details();
    loadAfsDetails();
  }, [afsServiceId]);

  useEffect(() => {
    window.scrollTo(0, 0); // 页面跳转时滚动到顶部
  }, []);

  if (loading) {
    return <UKPageLoading style={{ backgroundColor: 'transparent' }} />;
  }

  if (error) {
    return (
      <UKPageError
        style={{ padding: '60px 0' }}
        text={t('order.orderList.errorPage.tip')}
        btnText={t('order.orderList.errorPage.btnTip')}
        clickTryAgain={() => {
          window.location.reload();
        }}
      />
    );
  }

  if (!afsDetails) {
    return (
      <UKPageNothing
        style={{ padding: '60px 0' }}
        defaultImageType={DefaultImageType.DEFAULT_NOTING}
        text={t('order.orderList.emptyPage.returnTip')}
      />
    );
  }

  const recommendAddCart = (item: { skuId: string }) => {
    addCartUtil([{ skuId: item?.skuId, num: '1' }], true);
  };

  return (
    <div className={styles.afs_detail_wrap}>
      <Helmet>
        <title>{`${t('seo.title.requestRefund', {
          orderId: '#' + orderId || '',
        })} | Joybuy`}</title>

        <meta name='description' content={t('seo.description.requestRefund')} />
      </Helmet>
      <div className={styles.detail_inner_wrap} id='returnDetail_page_container'>
        {!!afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length && (
          <>
            <div
              className={cn(styles.step_outer_wrap, {
                [styles.step_outer_wrap3]:
                  afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length === 2,
                [styles.step_outer_wrap2]:
                  afsDetails?.afsServiceProcessNodeVo?.nodeStatusList?.length === 3,
              })}
            >
              <StepBar
                step={afsDetails?.afsServiceProcessNodeVo?.currentIndex || 0}
                stepList={afsDetails?.afsServiceProcessNodeVo?.nodeStatusList || []}
              />
            </div>
            <RuleWrap />
            {/* 新增样式：进度条和展示退款金额和优惠券 */}
            <div className={styles.afterSalesAccordion_wrap}>
              {/* <AfterSalesAccordion statusList={afsDetails?.afsServiceProcessNodeVo?.nodeStatusList || []} /> */}
              <AfterSalesAccordion
                afsDetailInfo={afsDetails?.afsServiceDetailVo}
                afsProcessNode={afsDetails?.afsServiceProcessNodeVo}
                onRefresh={loadAfsDetails}
              />
              {afsDetails?.afsServiceDetailVo?.refundAmountStr && (
                <RefundDetails
                  afsDetailInfo={afsDetails?.afsServiceDetailVo}
                  localInfo={afsDetails?.localInfo}
                />
              )}
            </div>
          </>
        )}

        <DetailProduct wareInfoDTOList={afsDetails?.afsServiceDetailVo?.skuList} />
        {/* 售后详情，展示上传图片和视频 */}
        <RefundInformation afsDetailInfo={afsDetails?.afsServiceDetailVo} />
      </div>
      {isLoaded && (
        <RecommendClient sceneCode={SceneCode.AFTER_SALES} addCartFn={recommendAddCart} />
      )}
    </div>
  );
});

export default AfterSales;
